import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState, Component, ErrorInfo, ReactNode } from 'react';
import { useAuthStore } from '@/store/auth.store';
import { Subscription } from '@haccp/api-client';
import i18n from '@/i18n';
import { ToastProvider } from '@/contexts/ToastContext';
import { ToastContainer } from '@/components/ToastContainer';
import { useApiClient } from '@/hooks/useApiClient';
import AuthLayout from '@/layouts/AuthLayout';
import DashboardLayout from '@/layouts/DashboardLayout';
import StaffLayout from '@/layouts/StaffLayout';
import AdminLayout from '@/layouts/AdminLayout';

// Global error logging for debugging
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    console.error('[Global Error]', event.message, 'at', event.filename, ':', event.lineno, ':', event.colno);
    console.error('[Global Error Stack]', event.error?.stack);
  });
  window.addEventListener('unhandledrejection', (event) => {
    console.error('[Unhandled Promise]', event.reason?.message || event.reason);
    console.error('[Unhandled Promise Stack]', event.reason?.stack);
  });
}

// Pages
import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import VerifyEmailPage from '@/pages/auth/VerifyEmailPage';
import VerificationPendingPage from '@/pages/auth/VerificationPendingPage';
import DashboardPage from '@/pages/dashboard/DashboardPage';
import TemperatureLogsPage from '@/pages/temperature-logs/TemperatureLogsPage';
import EquipmentPage from '@/pages/equipment/EquipmentPage';
import EquipmentConfigPage from '@/pages/equipment/EquipmentConfigPage';
import CleaningPage from '@/pages/cleaning/CleaningPage';
import CleaningConfigPage from '@/pages/cleaning/CleaningConfigPage';
import IncidentsPage from '@/pages/incidents/IncidentsPage';
import SupportPage from '@/pages/support/SupportPage';
import SettingsPage from '@/pages/settings/SettingsPage';
import OilPage from '@/pages/oil/OilPage';
import PmsPage from '@/pages/pms/PmsPage';
import ExportsPage from '@/pages/exports/ExportsPage';
import NotificationsPage from '@/pages/notifications/NotificationsPage';
import TraceabilityPage from '@/pages/traceability/TraceabilityPage';
import PreparationRecettesPage from '@/pages/traceability/PreparationRecettesPage';
import DLCBatchesPage from '@/pages/dlc/DLCBatchesPage';
import SuppliersPage from '@/pages/suppliers/SuppliersPage';
import ReceptionPage from '@/pages/reception/ReceptionPage';
import DLCScanPage from '@/pages/dlc/DLCScanPage';
import TempTrackingPage from '@/pages/temp-tracking/TempTrackingPage';
import TasksPage from '@/pages/tasks/TasksPage';
import TasksDonePage from '@/pages/tasks/TasksDonePage';
import StaffDashboardPage from '@/pages/staff/StaffDashboardPage';
import NotFoundPage from '@/pages/404/NotFoundPage';
import OnboardingPage from '@/pages/onboarding/OnboardingPage';

// Billing Pages
import PricingPage from '@/pages/billing/PricingPage';
import CheckoutSuccessPage from '@/pages/billing/CheckoutSuccessPage';

// Admin Pages
import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import AdminSubscriptionsPage from '@/pages/admin/AdminSubscriptionsPage';
import AdminEstablishmentsPage from '@/pages/admin/AdminEstablishmentsPage';
import AdminAuditLogsPage from '@/pages/admin/AdminAuditLogsPage';
import AdminBillingPage from '@/pages/admin/AdminBillingPage';
import AdminSupportPage from '@/pages/admin/AdminSupportPage';
import AdminCreateUsersPage from '@/pages/admin/AdminCreateUsersPage';

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Error:', error.message);
    console.error('[ErrorBoundary] Stack:', error.stack);
    console.error('[ErrorBoundary] Component Stack:', errorInfo.componentStack);
    console.error('[ErrorBoundary] Current URL:', window.location.pathname);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      const errorMsg = this.state.error?.message || '';
      const errorStack = this.state.error?.stack || '';
      const componentStack = this.state.errorInfo?.componentStack || '';
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-red-50 p-4">
          <h1 className="text-2xl font-bold text-red-600 mb-4">{i18n.t('common.errorOccurred')}</h1>
          <p className="text-sm text-red-500 mb-2">Page: {window.location.pathname}</p>
          <pre className="bg-white p-4 rounded border border-red-200 max-w-2xl overflow-auto text-sm text-red-800 max-h-60">
            {errorMsg}
          </pre>
          <details className="mt-2 max-w-2xl w-full">
            <summary className="cursor-pointer text-sm text-red-600">{i18n.t('common.technicalDetails')}</summary>
            <pre className="bg-white p-4 rounded border border-red-200 overflow-auto text-xs text-red-700 mt-1 max-h-40">
              {errorStack}
            </pre>
            <pre className="bg-white p-4 rounded border border-red-200 overflow-auto text-xs text-red-700 mt-1 max-h-40">
              {componentStack}
            </pre>
          </details>
          <button
            onClick={() => { this.setState({ hasError: false, error: null, errorInfo: null }); window.location.href = '/'; }}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            {i18n.t('common.backHome')}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

/**
 * Session restorer - restores user session from localStorage on app load
 */
function SessionRestorer() {
  const token = useAuthStore((state) => state.token);
  const hasHydrated = useAuthStore((state) => state._hasHydrated);
  const apiClient = useApiClient();

  useEffect(() => {
    if (hasHydrated && token) {
      apiClient.setToken(token);
    }
  }, [hasHydrated, token, apiClient]);

  return null;
}

/**
 * Protected route wrapper - checks auth AND active subscription AND onboarding
 * Redirects to /pricing if subscription is not ACTIVE
 * Redirects to /onboarding if OWNER user hasn't configured any equipment yet
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const hasHydrated = useAuthStore((state) => state._hasHydrated);
  const apiClient = useApiClient();
  const [onboardingNeeded, setOnboardingNeeded] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  const shouldCheckSubscription = !!token && hasHydrated && user?.role !== 'PLATFORM_ADMIN' && user?.role !== 'STAFF';
  const subscriptionQuery = useQuery<Subscription, Error>({
    queryKey: ['subscription'],
    queryFn: async () => apiClient.getSubscription(),
    enabled: shouldCheckSubscription,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const subStatus = user?.role === 'PLATFORM_ADMIN' ? 'ACTIVE' : subscriptionQuery.data?.status ?? null;
  const isSubLoading = shouldCheckSubscription ? subscriptionQuery.isLoading : false;
  const isSubError = shouldCheckSubscription ? subscriptionQuery.isError : false;

  useEffect(() => {
    if (!token || !user || user.role !== 'OWNER' || subStatus !== 'ACTIVE') {
      setOnboardingChecked(true);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const flag = localStorage.getItem(`onboarding_seen_${user.id}`);
        if (flag === 'true') {
          if (!cancelled) setOnboardingChecked(true);
          return;
        }

        const equipment = await apiClient.getEquipment();
        const list = Array.isArray(equipment) ? equipment : (equipment as any)?.equipment ?? [];
        if (list.length > 0) {
          localStorage.setItem(`onboarding_seen_${user.id}`, 'true');
          if (!cancelled) setOnboardingChecked(true);
        } else {
          if (!cancelled) {
            setOnboardingNeeded(true);
            setOnboardingChecked(true);
          }
        }
      } catch {
        if (!cancelled) setOnboardingChecked(true); // fail-open
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [subStatus, token, user, apiClient]);

  if (!hasHydrated || isSubLoading || !onboardingChecked) {
    return (
      <div className="flex items-center justify-center h-screen bg-neutral-50">
        <div className="text-neutral-600">Loading...</div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === 'STAFF') {
    return <Navigate to="/staff" replace />;
  }

  if (user?.role === 'PLATFORM_ADMIN') {
    return <Navigate to="/admin" replace />;
  }

  if (user?.emailVerified === false) {
    return <Navigate to="/verification-pending" replace />;
  }

  if (isSubError || subStatus !== 'ACTIVE') {
    return <Navigate to="/pricing" replace />;
  }

  if (onboardingNeeded) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

/**
 * Staff-only route wrapper — allows only STAFF role, redirects everyone else
 */
function StaffProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const hasHydrated = useAuthStore((state) => state._hasHydrated);
  const apiClient = useApiClient();

  const shouldCheckSubscription = !!token && hasHydrated && user?.role === 'STAFF';
  const subscriptionQuery = useQuery<Subscription, Error>({
    queryKey: ['subscription'],
    queryFn: async () => apiClient.getSubscription(),
    enabled: shouldCheckSubscription,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const subStatus = subscriptionQuery.data?.status ?? null;

  if (!hasHydrated) {
    return (
      <div className="flex items-center justify-center h-screen bg-neutral-50">
        <div className="text-neutral-600">Loading...</div>
      </div>
    );
  }

  if (!token) return <Navigate to="/login" replace />;
  if (user?.role !== 'STAFF') return <Navigate to="/" replace />;
  if (user?.emailVerified === false) return <Navigate to="/verification-pending" replace />;
  if (subscriptionQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-neutral-50">
        <div className="text-neutral-600">Loading...</div>
      </div>
    );
  }
  if (subscriptionQuery.isError || subStatus !== 'ACTIVE') return <Navigate to="/pricing" replace />;

  return <>{children}</>;
}

/**
 * Admin-only route wrapper
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const hasHydrated = useAuthStore((state) => state._hasHydrated);

  // Show loading while hydrating
  if (!hasHydrated) {
    return (
      <div className="flex items-center justify-center h-screen bg-neutral-50">
        <div className="text-neutral-600">Loading...</div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'PLATFORM_ADMIN') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <SessionRestorer />
            <Routes>
              {/* Auth routes */}
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
              </Route>

              {/* Email verification pending (authenticated but unverified) */}
              <Route path="/verification-pending" element={<VerificationPendingPage />} />

              {/* Onboarding wizard (authenticated OWNER, no equipment configured yet) */}
              <Route path="/onboarding" element={<OnboardingPage />} />

              {/* Billing routes (standalone pages, no sidebar) */}
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/checkout/success" element={<CheckoutSuccessPage />} />

              {/* Protected dashboard routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="tasks" element={<TasksPage />} />
                <Route path="tasks/done" element={<TasksDonePage />} />
                <Route path="temperature-logs" element={<TemperatureLogsPage />} />
                <Route path="equipment" element={<EquipmentPage />} />
                <Route path="equipment/config" element={<EquipmentConfigPage />} />
                <Route path="cleaning" element={<CleaningPage />} />
                <Route path="cleaning/config" element={<CleaningConfigPage />} />
                <Route path="incidents" element={<IncidentsPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="oil" element={<OilPage />} />
                <Route path="pms" element={<PmsPage />} />
                <Route path="exports" element={<ExportsPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="traceability" element={<TraceabilityPage />} />
                <Route path="traceability/preparations" element={<PreparationRecettesPage />} />
                <Route path="temp-tracking" element={<TempTrackingPage />} />
                <Route path="dlc" element={<DLCBatchesPage />} />
                <Route path="suppliers" element={<SuppliersPage />} />
                <Route path="reception" element={<ReceptionPage />} />
                <Route path="dlc/scan/:batchId" element={<DLCScanPage />} />
              </Route>

              {/* Staff routes — simplified tile interface for STAFF role only */}
              <Route
                element={
                  <StaffProtectedRoute>
                    <StaffLayout />
                  </StaffProtectedRoute>
                }
              >
                <Route path="/staff" element={<StaffDashboardPage />} />
                <Route path="/staff/temperatures" element={<TemperatureLogsPage />} />
                <Route path="/staff/cleaning" element={<CleaningPage />} />
                <Route path="/staff/reception" element={<ReceptionPage />} />
                <Route path="/staff/dlc" element={<PreparationRecettesPage />} />
                <Route path="/staff/temp-tracking" element={<TempTrackingPage />} />
                <Route path="/staff/traceability" element={<TraceabilityPage />} />
                <Route path="/staff/traceability/preparations" element={<PreparationRecettesPage />} />
                <Route path="/staff/oil" element={<OilPage />} />
                <Route path="/staff/incidents" element={<IncidentsPage />} />
                <Route path="/staff/tasks" element={<TasksPage />} />
                <Route path="/staff/notifications" element={<NotificationsPage />} />
                <Route path="/staff/support" element={<SupportPage />} />
              </Route>

              {/* Admin routes */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                }
              >
                <Route index element={<AdminDashboardPage />} />
                <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
                <Route path="establishments" element={<AdminEstablishmentsPage />} />
                <Route path="create-users" element={<AdminCreateUsersPage />} />
                <Route path="audit-logs" element={<AdminAuditLogsPage />} />
                <Route path="billing" element={<AdminBillingPage />} />
                <Route path="support" element={<AdminSupportPage />} />
              </Route>

              {/* 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <ToastContainer />
          </BrowserRouter>
        </QueryClientProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
